import React, { useEffect, useState, createElement } from 'react';
import { motion } from 'framer-motion';
import { GitHubPR, GitHubPRFile, GitHubReview, GitHubReviewComment, githubData } from '../../services/githubData';
import { XIcon, CheckCircle2Icon, XCircleIcon, MessageSquareIcon, FileIcon, GitPullRequestIcon, GitCommitIcon, AlertCircleIcon, SendIcon, CopyIcon } from 'lucide-react';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { SideBySideDiff } from '../shared/SideBySideDiff';
interface PRReviewPanelProps {
  pr: GitHubPR;
  onClose: () => void;
}
export function PRReviewPanel({
  pr,
  onClose
}: PRReviewPanelProps) {
  const [files, setFiles] = useState<GitHubPRFile[]>([]);
  const [reviews, setReviews] = useState<GitHubReview[]>([]);
  const [comments, setComments] = useState<GitHubReviewComment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reviewBody, setReviewBody] = useState('');
  const [reviewAction, setReviewAction] = useState<'APPROVE' | 'REQUEST_CHANGES' | 'COMMENT'>('COMMENT');
  const [submitting, setSubmitting] = useState(false);
  useEffect(() => {
    async function fetchData() {
      try {
        setLoading(true);
        const [fetchedFiles, fetchedReviews, fetchedComments] = await Promise.all([githubData.getPRFiles(pr.repoName, pr.number), githubData.getPRReviews(pr.repoName, pr.number), githubData.getPRComments(pr.repoName, pr.number)]);
        setFiles(fetchedFiles);
        setReviews(fetchedReviews);
        setComments(fetchedComments);
      } catch (err: any) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, [pr]);
  const handleSubmitReview = async () => {
    if (!reviewBody && reviewAction !== 'APPROVE') return;
    try {
      setSubmitting(true);
      await githubData.submitPRReview(pr.repoName, pr.number, reviewAction, reviewBody);
      // Refresh reviews
      const fetchedReviews = await githubData.getPRReviews(pr.repoName, pr.number);
      setReviews(fetchedReviews);
      setReviewBody('');
      setReviewAction('COMMENT');
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: 1,
    y: 0
  }} exit={{
    opacity: 0,
    y: 20
  }} className="fixed inset-0 z-50 flex flex-col bg-slate-950/95 backdrop-blur-xl" data-id="element-6039">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-slate-800 bg-slate-900/50" data-id="element-6040">
        <div className="flex items-center gap-4" data-id="element-6041">
          <div className="p-2 bg-slate-800 rounded-lg" data-id="element-6042">
            <GitPullRequestIcon size={20} className="text-indigo-400" data-id="element-6043" />
          </div>
          <div data-id="element-6044">
            <h2 className="text-lg font-bold text-slate-100 flex items-center gap-2" data-id="element-6045">
              {pr.title}{' '}
              <span className="text-slate-500 font-normal" data-id="element-6046">#{pr.number}</span>
            </h2>
            <div className="flex items-center gap-3 text-sm text-slate-400 mt-1" data-id="element-6047">
              <span className="flex items-center gap-1.5" data-id="element-6048">
                <img src={pr.user.avatar_url} alt="" className="w-4 h-4 rounded-full" data-id="element-6049" />

                {pr.user.login}
              </span>
              <span data-id="element-6050">·</span>
              <span className="text-emerald-400" data-id="element-6051">+{pr.additions}</span>
              <span className="text-red-400" data-id="element-6052">-{pr.deletions}</span>
              <span data-id="element-6053">·</span>
              <span data-id="element-6054">{pr.changed_files} files</span>
            </div>
          </div>
        </div>
        <button onClick={onClose} className="p-2 text-slate-400 hover:text-slate-200 hover:bg-slate-800 rounded-lg transition-colors" data-id="element-6055">
          <XIcon size={20} data-id="element-6056" />
        </button>
      </div>

      <div className="flex-1 overflow-hidden flex" data-id="element-6057">
        {/* Left: Diff Viewer */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6" data-id="element-6058">
          {loading ? <div className="flex items-center justify-center h-full text-slate-500 gap-2" data-id="element-6059">
              <div className="w-4 h-4 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" data-id="element-6060" />
              Cargando diff...
            </div> : error ? <div className="p-4 bg-red-950/40 border border-red-900/50 rounded-xl text-red-400 flex items-start gap-3" data-id="element-6061">
              <AlertCircleIcon size={20} className="shrink-0 mt-0.5" data-id="element-6062" />
              <p data-id="element-6063">{error}</p>
            </div> : files.map(file => {
          const [fileCopied, setFileCopied] = useState(false);
          const handleFileCopy = () => {
            const text = file.patch || '';
            if (navigator.clipboard && window.isSecureContext) {
              navigator.clipboard.writeText(text).then(() => {
                setFileCopied(true);
                setTimeout(() => setFileCopied(false), 2000);
              }).catch(() => {});
            } else {
              const ta = document.createElement('textarea');
              ta.value = text;
              ta.style.position = 'fixed';
              ta.style.opacity = '0';
              document.body.appendChild(ta);
              ta.select();
              try {
                document.execCommand('copy');
              } catch {}
              document.body.removeChild(ta);
              setFileCopied(true);
              setTimeout(() => setFileCopied(false), 2000);
            }
          };
          return <div key={file.sha} className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden shadow-sm" data-id="element-6064">
                  <div className="flex items-center justify-between px-4 py-2.5 border-b border-slate-800 bg-slate-900/80" data-id="element-6065">
                    <div className="flex items-center gap-2 text-sm font-medium text-slate-300" data-id="element-6066">
                      <FileIcon size={14} className="text-slate-500" data-id="element-6067" />
                      {file.filename}
                    </div>
                    <div className="flex items-center gap-2 text-xs font-mono" data-id="element-6068">
                      <span className="text-emerald-400" data-id="element-6069">
                        +{file.additions}
                      </span>
                      <span className="text-red-400" data-id="element-6070">-{file.deletions}</span>
                      <button onClick={handleFileCopy} className="p-1 rounded-md text-slate-500 hover:text-slate-300 hover:bg-slate-800 transition-colors ml-1" title="Copiar diff" data-id="element-6071">
                        {fileCopied ? <CheckCircle2Icon size={13} className="text-emerald-400" data-id="element-6072" /> : <CopyIcon size={13} data-id="element-6073" />}
                      </button>
                    </div>
                  </div>
                  <div data-id="element-6074">
                    {file.patch ? <SideBySideDiff patch={file.patch} maxHeight={500} filename={file.filename} data-id="element-6075" /> : <div className="px-4 py-2 text-xs text-slate-500 italic bg-[#0d1117]" data-id="element-6076">
                        Binary file or too large to show diff.
                      </div>}
                  </div>
                </div>;
        })}
        </div>

        {/* Right: Review Panel */}
        <div className="w-96 border-l border-slate-800 bg-slate-900/30 flex flex-col" data-id="element-6077">
          <div className="p-4 border-b border-slate-800 font-semibold text-slate-200" data-id="element-6078">
            Review Summary
          </div>

          <div className="flex-1 overflow-y-auto p-4 space-y-4" data-id="element-6079">
            {reviews.length === 0 ? <p className="text-sm text-slate-500 text-center py-8" data-id="element-6080">
                No reviews yet.
              </p> : reviews.map(review => <div key={review.id} className="bg-slate-900 border border-slate-800 rounded-xl p-3" data-id="element-6081">
                  <div className="flex items-center justify-between mb-2" data-id="element-6082">
                    <div className="flex items-center gap-2 text-sm font-medium text-slate-200" data-id="element-6083">
                      <img src={review.user.avatar_url} alt="" className="w-5 h-5 rounded-full" data-id="element-6084" />

                      {review.user.login}
                    </div>
                    {review.state === 'APPROVED' && <Badge variant="success" className="text-[10px]" data-id="element-6085">
                        <CheckCircle2Icon size={10} className="mr-1" data-id="element-6086" /> Approved
                      </Badge>}
                    {review.state === 'CHANGES_REQUESTED' && <Badge variant="error" className="text-[10px]" data-id="element-6087">
                        <XCircleIcon size={10} className="mr-1" data-id="element-6088" /> Changes
                      </Badge>}
                    {review.state === 'COMMENTED' && <Badge variant="outline" className="text-[10px]" data-id="element-6089">
                        <MessageSquareIcon size={10} className="mr-1" data-id="element-6090" /> Comment
                      </Badge>}
                  </div>
                  {review.body && <p className="text-sm text-slate-400 mt-2" data-id="element-6091">{review.body}</p>}
                </div>)}
          </div>

          {/* Submit Review Form */}
          <div className="p-4 border-t border-slate-800 bg-slate-900" data-id="element-6092">
            <h3 className="text-sm font-medium text-slate-200 mb-3" data-id="element-6093">
              Submit Review
            </h3>

            <div className="flex gap-2 mb-3" data-id="element-6094">
              <button onClick={() => setReviewAction('COMMENT')} className={`flex-1 py-1.5 text-xs font-medium rounded-md transition-colors border ${reviewAction === 'COMMENT' ? 'bg-slate-800 text-slate-200 border-slate-700' : 'bg-transparent text-slate-500 border-transparent hover:bg-slate-800/50'}`} data-id="element-6095">
                Comment
              </button>
              <button onClick={() => setReviewAction('APPROVE')} className={`flex-1 py-1.5 text-xs font-medium rounded-md transition-colors border ${reviewAction === 'APPROVE' ? 'bg-emerald-900/30 text-emerald-400 border-emerald-800/50' : 'bg-transparent text-slate-500 border-transparent hover:bg-slate-800/50'}`} data-id="element-6096">
                Approve
              </button>
              <button onClick={() => setReviewAction('REQUEST_CHANGES')} className={`flex-1 py-1.5 text-xs font-medium rounded-md transition-colors border ${reviewAction === 'REQUEST_CHANGES' ? 'bg-red-900/30 text-red-400 border-red-800/50' : 'bg-transparent text-slate-500 border-transparent hover:bg-slate-800/50'}`} data-id="element-6097">
                Request Changes
              </button>
            </div>

            <textarea value={reviewBody} onChange={e => setReviewBody(e.target.value)} placeholder="Leave a comment..." className="w-full h-24 bg-slate-950 border border-slate-800 rounded-lg p-3 text-sm text-slate-200 placeholder:text-slate-600 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 resize-none mb-3" data-id="element-6098" />

            <Button variant={reviewAction === 'APPROVE' ? 'primary' : 'secondary'} className="w-full gap-2" onClick={handleSubmitReview} disabled={submitting || !reviewBody && reviewAction !== 'APPROVE'} data-id="element-6099">
              {submitting ? <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" data-id="element-6100" /> : <SendIcon size={16} data-id="element-6101" />}
              Submit{' '}
              {reviewAction === 'APPROVE' ? 'Approval' : reviewAction === 'REQUEST_CHANGES' ? 'Changes Request' : 'Comment'}
            </Button>
          </div>
        </div>
      </div>
    </motion.div>;
}